/**
 * Defined benefit pension arithmetic used by the wizard captions.
 *
 * A small client-side mirror of server/src/services/projectionEngine/dbPension.ts
 * (the client can only import server *types*). Keep the two in step:
 *   - early/late multiplier: 1 − r × yearsEarly (floor 0.20), 1 + r × yearsLate (cap 1.50)
 *   - HMRC maximum commutation at factor f: c ≤ 20P / (3f + 20)
 * The 0.20 floor and 1.50 cap are input guards, not scheme rules — never
 * present them as such in copy.
 */

export const DEFAULT_EARLY_REDUCTION_PCT_PER_YEAR = 5;
export const DEFAULT_LATE_INCREASE_PCT_PER_YEAR = 5;
export const DEFAULT_COMMUTATION_FACTOR = 12;
const MIN_ADJUSTMENT_MULTIPLIER = 0.2;
const MAX_ADJUSTMENT_MULTIPLIER = 1.5;

/** Early/late multiplier for a pension drawn `yearsFromNpa` whole years from normal pension age (negative = early). */
export function dbAdjustmentMultiplier(
  yearsFromNpa: number,
  earlyReductionPctPerYear: number = DEFAULT_EARLY_REDUCTION_PCT_PER_YEAR,
  lateIncreasePctPerYear: number = DEFAULT_LATE_INCREASE_PCT_PER_YEAR,
): number {
  if (yearsFromNpa < 0) {
    return Math.max(MIN_ADJUSTMENT_MULTIPLIER, 1 - (earlyReductionPctPerYear / 100) * -yearsFromNpa);
  }
  if (yearsFromNpa > 0) {
    return Math.min(MAX_ADJUSTMENT_MULTIPLIER, 1 + (lateIncreasePctPerYear / 100) * yearsFromNpa);
  }
  return 1;
}

/**
 * Pension after the early/late adjustment: the headline amount when there is
 * no normal pension age or no resolvable start age, else amount × multiplier.
 */
export function adjustedDbPensionAmount(
  annualAmount: number,
  normalPensionAge: number | undefined,
  startAge: number | undefined,
  earlyReductionPctPerYear?: number,
  lateIncreasePctPerYear?: number,
): number {
  if (normalPensionAge == null || startAge == null) return annualAmount;
  return annualAmount * dbAdjustmentMultiplier(startAge - normalPensionAge, earlyReductionPctPerYear, lateIncreasePctPerYear);
}

/** HMRC maximum pension that can be given up for tax-free cash at commutation factor `factor`. */
export function maxCommutablePension(adjustedAmount: number, factor: number): number {
  if (!(adjustedAmount > 0) || !(factor > 0)) return 0;
  return (20 * adjustedAmount) / (3 * factor + 20);
}

/**
 * Age at 1 January of the plan's calendar year — the same convention the
 * engine uses to seed each person's age — plus one per projection year.
 * Undefined when either date is unusable.
 */
export function ageInPlanYear(dateOfBirth: string, planAsOfDate: string, planYear: number): number | undefined {
  const planStartYear = Number.parseInt(planAsOfDate.slice(0, 4), 10);
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(planStartYear) || Number.isNaN(dob.getTime()) || !Number.isInteger(planYear)) return undefined;
  const asOf = new Date(planStartYear, 0, 1);
  let age = asOf.getFullYear() - dob.getFullYear();
  const monthDiff = asOf.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < dob.getDate())) age--;
  return age + planYear;
}
